/* eslint-disable complexity */
import React, { useState, useContext, useEffect, useRef, ElementRef } from 'react';
import uuid from 'uuid-random';
import { navigate, useParams } from '@reach/router';
import { Checkbox } from 'react-magma-dom';
import { isEmpty } from 'lodash';
import { ActionTypes, TextPresentationPageContext } from '../../contexts/textPresentationPageContext';
import { insertDraggableNote } from '../DraggableLabel/ContextDraggableNote';
import { useMainContentSize } from '../../hooks/useMainContentSize';
import { LaterExemplar, EmergentExemplar, ExemplarCombined, ILabel, WordsPageContent } from '../../types';
import {
  StyledEditTool,
  StyledLabelIcon,
  StyledLabelTool,
  StyledToolLabel,
  StyledToolLabelText,
  StyledBorderWrapper,
  StyledLabelTooltip,
} from '../Styled';
import { ToolButton } from './ToolButton';
import {
  isTouchDevice,
  EDIT_TOOL,
  DEFAULT_STATE,
  getEditLabel,
  getEditIcon,
  computeSavedPenLines,
  computeSavedNotes,
  getLabelsToShow,
  getImagesToShow,
  findEmergentContentByPage,
  getStageColors,
} from '../../utils';
import { Tool, TOOLS } from '../../constants/edit';
import { ColorPicker } from '../ColorPicker';
import { COLORS, COLOR_PICKER, DRAWING_TOOL_COLORS, EMERGENT } from '../../constants';
import { SAVED_EXEMPLAR, WORDS } from '../../constants/navigation';
import { DrawingContext } from '../../contexts/drawingContext';
import { StyledWordContext } from '../../contexts/styledWordContext';
import { EditButtonsWrapper, SaveCloseButtonsWrapper, ToolsContainer } from '../Styled/StyledWrappers';
import { RemoveRedEyeIcon, SetRedEyeIcon, label as redEyeLabel } from '../../assets/icons/RedEye';
import { ResetModal } from '../Modal/ResetModal';
import { SaveTool } from './SaveTool';
import { LeaveEditingModal } from '../Modal/LeaveEditingModal';
import { TextEditor } from './TextEditor';
import { EditToolContext } from '../../contexts/editToolContext';
import { WordsPageContext } from '../../contexts/wordsPageContext';
import { exemplarApi } from '../../API/exemplar';
import useBeforeUnload from '../../hooks/useBeforeUnload';
import {
  AddLabelLimitReached,
  ExemplarContextCombined,
  getEditedExemplarContent,
  getExemplarContext,
  hasHighlightsInContent,
} from '../../utils/editTool';
import { LIMIT_DRAG_ELEMENTS } from '../DraggableLabel/ContextDraggableLabel';
import { UndoRedoActions, UndoRedoContext } from '../../contexts/undoRedoContext';
import { Type } from '../../hooks/useUndoRedo';

interface IEditTool {
  mode: string;
  labels?: ILabel[];
  isAnyLabelShown?: boolean;
  isNotesExist?: boolean;
  handleNotesVisibility?: () => void;
  addLabel?: () => void;
  labelClick?: (id: number) => void;
  handleHighlightingTool?: () => void;
  allLabelsClick?: (checked: boolean) => void;
  onLabelOn?: (id: number) => void;
  onLabelOut?: () => void;
  onAllLabels?: () => void;
  handleColorPick?: (color: string) => void;
  handleHideHighlights?: () => void;
  handleHideText?: (hide: boolean) => void;
  panelBgColor: string;
  toolBgColor: string;
  lightColor: string;
  onClose: () => void;
  isFullScreen: boolean;
  exemplar: ExemplarCombined;
  exemplarId: number;
  exemplarType?: string;
  stage: string;
}

export const EditTool: React.FC<IEditTool> = (props): JSX.Element => {
  const {
    mode,
    addLabel,
    labels,
    isNotesExist,
    handleNotesVisibility,
    labelClick,
    allLabelsClick,
    isAnyLabelShown,
    onLabelOn,
    onLabelOut,
    handleHighlightingTool,
    handleColorPick,
    handleHideHighlights,
    handleHideText,
    panelBgColor,
    toolBgColor,
    lightColor,
    onClose,
    exemplar,
    exemplarId,
    exemplarType,
    stage,
  } = props;

  const { screen } = useParams();
  const [x, y] = useMainContentSize();
  const { state, dispatch, saveNotesVisibilityUpdates, saveAddNoteUpdates } = useContext(TextPresentationPageContext);
  const { state: wordsPageState, setLabels, setImages } = useContext(WordsPageContext);
  const isNotesManuallyHidden = state.notes[0]?.isManuallyHidden;
  const {
    highlighted,
    setHighlighted,
    colorPickerPosition,
    setColorPickerPosition,
    isColorsSectionVisible,
    setColorsSectionVisible,
    isDrawingToolActive,
    setDrawingToolActive,
    isLabelsSectionVisible,
    setLabelsSectionVisible,
  } = useContext(EditToolContext);
  const { styledWordState, handleImageTool, clearStyledWords, saveHideHighlightsUpdates, saveTextVisibilityUpdates } =
    useContext(StyledWordContext);
  const {
    lines,
    isPen,
    setPen,
    drawingColor,
    setDrawingColor,
    areDrawingsHidden,
    hideDrawings,
    clearDrawings,
    saveDrawingsHiddenUpdates,
  } = useContext(DrawingContext);
  const { events, isEventsNotEmpty, undoneEvents, undo, redo, clearEvents, getLastEventName } =
    useContext(UndoRedoContext);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const saveToolRef = useRef<ElementRef<typeof SaveTool>>(null);

  const { isLeaveModalShown, handleLeavePage, closeLeaveModal, preventedAction, clearEditTool } =
    useContext(EditToolContext);
  const { exemplarState, setExemplar, setSavedEdits, saveTextUpdates } = useContext(
    getExemplarContext(stage) as ExemplarContextCombined
  );

  const isSavedExemplar = exemplarType === SAVED_EXEMPLAR;

  const isAnyLabelOnScreen = !!labels && labels?.length;
  const isTextHidden = styledWordState.isHiddenTextMode;
  const isAnyWordHighlighted = hasHighlightsInContent(exemplarState, stage, mode);
  const isLabelsLimitReached = wordsPageState.labels.length >= LIMIT_DRAG_ELEMENTS;
  const ariaLabelForAllLabels = `click to ${isAnyLabelShown ? 'show' : 'hide'} all labels`;
  const colorPickerIconWidth = 15;
  const colorPickerContainerPaddings = 14;
  const colorPickerPaddingRight = 8;
  const colorPickerTop = 3;

  const editToolButtonWidth = 42;

  const hideLabelContainerWidth = 200;

  const handleHighlights = (): void => {
    disableDrawingTool();
    handleHighlighted();
    setHighlighted({
      ...highlighted,
      pen: false,
      highlight: false,
      hideHighlights: false,
      imageEditor: false,
    });
  };

  useEffect(() => {
    if (styledWordState.imageTool.isVisible) {
      setHighlighted({
        ...highlighted,
        imageEditor: false,
      });
      handleImageTool(false);
    }

    handleHighlights();
  }, [mode]);

  useEffect(() => {
    setColorsSectionVisible(isColorsSectionVisible);
    setDrawingToolActive(isDrawingToolActive);
    setLabelsSectionVisible(isLabelsSectionVisible);
    setHighlighted(highlighted);
    handleHighlights();
  }, [screen]);

  const setSavedTools = (highlighted: Record<string, boolean>) => {
    const { tools } = (exemplar as LaterExemplar | EmergentExemplar) || {};

    if (tools) {
      let highlightedState = { ...highlighted };
      const { isTextHidden, pen, notes, highlights } = tools;

      if (isTextHidden) {
        highlightedState = {
          ...highlightedState,
          [TOOLS.hideText.name]: true,
        };
      }

      if (pen) {
        const { isHidden } = pen;

        if (isHidden) {
          highlightedState = {
            ...highlightedState,
            [TOOLS.hideDrawings.name]: true,
            pen: false,
          };
        }
      }

      if (notes) {
        if (notes[0]?.isHidden) {
          highlightedState = {
            ...highlightedState,
            [TOOLS.hideNotes.name]: true,
          };
        }
      }

      if (highlights?.isHidden) {
        highlightedState = {
          ...highlightedState,
          [TOOLS.hideHighlights.name]: true,
        };
      }

      setHighlighted(highlightedState);
    }
  };

  useEffect(() => {
    setSavedTools(highlighted);
  }, [exemplar]);

  const showHideAllLabels = (count: number) => {
    if (labels) {
      return (
        labels.filter(label => {
          return !label.isHidden;
        }).length > count
      );
    }
  };

  const handleHideLabelClick = (offsetParent: HTMLElement) => {
    if (!isAnyLabelOnScreen) {
      return;
    }

    if (styledWordState.imageTool.isVisible) {
      handleImageTool(false);
    }

    const { offsetLeft, offsetHeight } = (offsetParent || {}) as HTMLElement;
    handleButtonClick(TOOLS.hideLabel.name);
    setColorPickerPosition({
      x: offsetLeft - hideLabelContainerWidth / 2 + editToolButtonWidth / 2,
      y: offsetHeight + colorPickerTop,
    });
    disableDrawingTool();
    setLabelsSectionVisible(!isLabelsSectionVisible);
  };

  const defaultFunction = () => {
    null;
  };

  const disableDrawingTool = () => {
    setPen(false);
    setDrawingToolActive(false);
  };

  const handleButtonClick = (buttonType: string) => {
    switch (buttonType) {
      case TOOLS.addLabel.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
        });
        break;
      case TOOLS.highlight.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [buttonType]: !highlighted[buttonType],
        });
        break;
      case TOOLS.hideHighlights.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [buttonType]: !highlighted[buttonType],
        });
        break;
      case TOOLS.pen.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [TOOLS.hideHighlights.name]: highlighted[TOOLS.hideHighlights.name],
          [buttonType]: !highlighted[buttonType],
        });
        break;
      case TOOLS.hideText.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [TOOLS.hideHighlights.name]: highlighted[TOOLS.hideHighlights.name],
          [buttonType]: !highlighted[buttonType],
        });
        break;
      case TOOLS.hideLabel.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [TOOLS.hideHighlights.name]: highlighted[TOOLS.hideHighlights.name],
          [buttonType]: !highlighted[buttonType],
        });
        break;
      case TOOLS.addNote.name:
        setHighlighted({ ...DEFAULT_STATE });
        break;
      case TOOLS.hideNotes.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [TOOLS.highlight.name]: highlighted[TOOLS.highlight.name],
          [TOOLS.hideHighlights.name]: highlighted[TOOLS.hideHighlights.name],
          [buttonType]: !highlighted[buttonType],
        });
        break;
      case TOOLS.hideDrawings.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [TOOLS.highlight.name]: highlighted[TOOLS.highlight.name],
          [TOOLS.hideHighlights.name]: highlighted[TOOLS.hideHighlights.name],
          [buttonType]: !areDrawingsHidden,
        });
        break;
      case TOOLS.imageEditor.name:
        setHighlighted({
          ...DEFAULT_STATE,
          [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
          [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
          [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
          [TOOLS.hideHighlights.name]: highlighted[TOOLS.hideHighlights.name],
          [buttonType]: !highlighted[buttonType],
        });
        break;
      default:
        setHighlighted({ ...DEFAULT_STATE, [buttonType]: !highlighted[buttonType] });
        break;
    }
  };

  const highlight = (offsetParent: HTMLElement) => {
    const { offsetLeft, offsetHeight } = (offsetParent as HTMLElement) || {};
    setColorPickerPosition({
      x:
        offsetLeft +
        21 -
        (colorPickerContainerPaddings +
          COLOR_PICKER.length * colorPickerIconWidth +
          (COLOR_PICKER.length - 1) * colorPickerPaddingRight) /
          2,
      y: offsetHeight + colorPickerTop,
    });
    handleButtonClick(TOOLS.highlight.name);
    if (isDrawingToolActive) {
      disableDrawingTool();
    }

    setColorsSectionVisible(!isColorsSectionVisible);
    handleHighlightingTool && handleHighlightingTool();
  };

  const hideHighlights = () => {
    handleButtonClick(TOOLS.hideHighlights.name);

    if (isDrawingToolActive) {
      disableDrawingTool();
    }

    if (styledWordState.imageTool.isVisible) {
      handleImageTool(false);
    }

    if (isColorsSectionVisible) {
      setColorsSectionVisible(false);
    }

    handleHideHighlights && handleHideHighlights();
  };

  const handleHighlighted = () => {
    if (highlighted['highlight']) {
      setColorsSectionVisible(false);
      handleHighlightingTool && handleHighlightingTool();
    }
  };

  const hideNotes = () => {
    handleHighlighted();
    handleButtonClick(TOOLS.hideNotes.name);

    disableDrawingTool();
    setColorsSectionVisible(false);
    setPen(false);
    handleNotesVisibility && handleNotesVisibility();

    saveNotesVisibilityUpdates(
      state.notes,
      state.notes.map(note => ({
        ...note,
        isManuallyHidden: !isNotesManuallyHidden,
      }))
    );

    setHighlighted({
      ...DEFAULT_STATE,
      hideNotes: !isNotesManuallyHidden,
      hideDrawings: areDrawingsHidden,
      hideHighlights: styledWordState.isHideHighlightsMode,
      hideText: isTextHidden,
    });
  };

  const startDrawing = (offsetParent: HTMLElement) => {
    const { offsetLeft, offsetHeight } = (offsetParent as HTMLElement) || {};
    setColorPickerPosition({
      x:
        offsetLeft +
        editToolButtonWidth / 2 -
        (colorPickerContainerPaddings +
          DRAWING_TOOL_COLORS.length * colorPickerIconWidth +
          (DRAWING_TOOL_COLORS.length - 1) * colorPickerPaddingRight) /
          2,
      y: offsetHeight + colorPickerTop,
    });
    handleHighlighted();

    handleButtonClick(TOOLS.pen.name);
    setPen(!isPen);
    setDrawingToolActive(!isDrawingToolActive);
  };

  const handleDrawingToolColor = (color: string) => {
    setDrawingColor(color);
  };

  const handleNoteClick = () => {
    handleHighlighted();
    handleButtonClick(TOOLS.addNote.name);

    setColorsSectionVisible(false);
    setPen(false);

    disableDrawingTool();

    insertDraggableNote(state, dispatch, x, saveAddNoteUpdates);

    setHighlighted({
      ...DEFAULT_STATE,
      hideNotes: highlighted.notes,
      hideDrawings: areDrawingsHidden,
      hideHighlights: styledWordState.isHideHighlightsMode,
      hideText: isTextHidden,
    });
  };

  const handleHideDrawings = () => {
    handleButtonClick(TOOLS.hideDrawings.name);
    hideDrawings();

    if (isDrawingToolActive) {
      disableDrawingTool();
    }

    if (styledWordState.imageTool.isVisible) {
      handleImageTool(false);
    }
  };

  const hideText = () => {
    if (styledWordState.imageTool.isVisible) {
      setHighlighted({ ...highlighted, [TOOLS.imageEditor.name]: false });
      handleImageTool(false);
    }

    handleButtonClick(TOOLS.hideText.name);
    handleHideText && handleHideText(!isTextHidden);
  };

  const handleUndoClick = () => {
    const lastUndoEventName = getLastEventName(UndoRedoActions.undo);

    switch (lastUndoEventName) {
      case Type.hideDrawings: {
        handleHideDrawings();
        break;
      }
      case Type.hideHighlights: {
        handleButtonClick(TOOLS.hideHighlights.name);
        break;
      }
      case Type.hideNotes: {
        handleButtonClick(TOOLS.hideNotes.name);
        break;
      }
      case Type.hideText: {
        handleButtonClick(TOOLS.hideText.name);
        break;
      }
      case Type.addNote:
      case Type.updateNoteContent:
      case Type.removeNote: {
        if (isDrawingToolActive) {
          handleButtonClick(TOOLS.pen.name);
          setPen(false);
          setDrawingToolActive(false);
        }
        if (isColorsSectionVisible) {
          handleButtonClick(TOOLS.highlight.name);
          handleHighlighted();
        }
        break;
      }
    }

    undo();
  };

  const handleRedoClick = () => {
    const lastRedoEventName = getLastEventName(UndoRedoActions.redo);

    switch (lastRedoEventName) {
      case TOOLS.hideHighlights.name:
        !highlighted[TOOLS.hideHighlights.name] && handleButtonClick(TOOLS.hideHighlights.name);
        break;
      case TOOLS.hideDrawings.name:
        handleButtonClick(TOOLS.hideDrawings.name);
        break;
      case TOOLS.hideNotes.name:
        handleButtonClick(TOOLS.hideNotes.name);
        break;
      case TOOLS.hideText.name:
        handleButtonClick(TOOLS.hideText.name);
        break;
    }

    redo();
  };

  const handleImageEditorClick = (): void => {
    isDrawingToolActive && disableDrawingTool();
    handleHighlighted();
    isLabelsSectionVisible && setLabelsSectionVisible(false);

    handleButtonClick(TOOLS.imageEditor.name);
    handleImageTool(!styledWordState.imageTool.isVisible);
  };

  const clearNotes = () => {
    dispatch({ type: ActionTypes.SET_NOTES, payload: [] });
  };

  const clearEdits = (keepEditMode = false) => {
    clearEvents();
    clearEditTool(keepEditMode);

    setExemplar(exemplar as EmergentExemplar & LaterExemplar, true);

    clearDrawings(mode);
    setDrawingToolActive(false);

    clearStyledWords();

    clearNotes();

    if (stage === EMERGENT) {
      const wordsPageContent = findEmergentContentByPage(
        exemplar?.content as EmergentExemplar['content'],
        WORDS
      ) as WordsPageContent;

      setLabels(getLabelsToShow(wordsPageContent?.words || [], wordsPageState.images, x, y));
      setImages(getImagesToShow(wordsPageContent?.images));
    }

    setLabelsSectionVisible(false);
    setColorsSectionVisible(false);
    setColorPickerPosition({ x: 0, y: 0 });
    setHighlighted(DEFAULT_STATE);

    setSavedEdits(exemplar as EmergentExemplar & LaterExemplar, mode);
  };

  const openSaveModal = () => {
    setShowSaveModal(true);
  };

  const closeSaveModal = () => {
    setShowSaveModal(false);
  };

  const openTextEditor = () => {
    setShowTextEditor(true);
  };

  const closeTextEditor = () => {
    setShowTextEditor(false);
  };

  const handleEditText = (editedExemplar: ExemplarCombined) => {
    saveTextUpdates(exemplarState, editedExemplar as EmergentExemplar & LaterExemplar);

    setExemplar(editedExemplar as EmergentExemplar & LaterExemplar, true);
  };

  const getEditedContent = () => {
    return getEditedExemplarContent(
      stage,
      exemplarState.content,
      exemplarState.title,
      styledWordState.images,
      wordsPageState.images,
      wordsPageState.labels
    );
  };

  const getTools = () => {
    return {
      pen: {
        lines: computeSavedPenLines(lines[mode]),
        isHidden: areDrawingsHidden,
      },
      notes: computeSavedNotes(state.notes),
      highlights: {
        isHidden: styledWordState.isHideHighlightsMode,
      },
      isTextHidden: styledWordState.isHiddenTextMode,
    };
  };

  const saveAsText = (textName: string) => {
    const editedContent = getEditedContent();
    const tools = getTools();

    const payload = {
      id: exemplarId,
      title: textName,
      page: mode,
      content: editedContent,
      tools,
    };

    return exemplarApi[isSavedExemplar ? 'saveExemplarFromSaved' : 'saveExemplar'](payload);
  };

  const update = () => {
    const editedContent = getEditedContent();
    const tools = getTools();

    const payload = {
      id: exemplarId,
      content: editedContent,
      tools,
    };

    return exemplarApi.updateSavedExemplar(payload);
  };

  const functions: Record<string, (e: MouseEvent | KeyboardEvent) => void> = {
    editText: () => {
      openTextEditor();

      if (isDrawingToolActive) {
        disableDrawingTool();
      }

      if (styledWordState.imageTool.isVisible) {
        handleImageTool(false);
      }

      setColorsSectionVisible(false);
      handleHighlighted();

      setHighlighted({
        ...highlighted,
        hideNotes: highlighted.notes,
        pen: false,
        highlight: false,
        hideHighlights: false,
        imageEditor: false,
      });
    },
    addLabel: () => {
      if (isDrawingToolActive) {
        disableDrawingTool();
      }

      if (styledWordState.imageTool.isVisible) {
        handleButtonClick(TOOLS.imageEditor.name);
        handleImageTool(false);
      }

      handleButtonClick(TOOLS.addLabel.name);

      addLabel ? addLabel() : defaultFunction();
    },
    hideLabel: (e: MouseEvent | KeyboardEvent) => {
      const target = e.currentTarget as HTMLElement;
      handleHideLabelClick(target.offsetParent as HTMLElement);
    },
    hideNotes,
    addNote: () => {
      if (styledWordState.imageTool.isVisible) {
        handleButtonClick(TOOLS.imageEditor.name);
        handleImageTool(false);
      }

      handleNoteClick();
    },
    highlight: (e: MouseEvent | KeyboardEvent) => {
      if (styledWordState.imageTool.isVisible) {
        handleImageTool(false);
      }

      const target = e.currentTarget as HTMLElement;
      highlight(target.offsetParent as HTMLElement);
    },
    hideHighlights: () => {
      hideHighlights();
      saveHideHighlightsUpdates(styledWordState.isHideHighlightsMode, !styledWordState.isHideHighlightsMode);
    },
    pen: (e: MouseEvent | KeyboardEvent) => {
      if (isLabelsSectionVisible) {
        setLabelsSectionVisible(false);
      }

      if (styledWordState.imageTool.isVisible) {
        handleImageTool(false);
      }

      const target = e.currentTarget as HTMLElement;
      startDrawing(target.offsetParent as HTMLElement);
    },
    hideDrawings: () => {
      handleHideDrawings();
      saveDrawingsHiddenUpdates(areDrawingsHidden, !areDrawingsHidden);
    },
    reset: () => {
      isDrawingToolActive && disableDrawingTool();
      isColorsSectionVisible && setColorsSectionVisible(false);
      isLabelsSectionVisible && setLabelsSectionVisible(false);
      styledWordState.imageTool.isVisible && handleImageTool(false);

      handleHighlighted();

      setHighlighted({
        ...DEFAULT_STATE,
        [TOOLS.hideNotes.name]: highlighted[TOOLS.hideNotes.name],
        [TOOLS.hideDrawings.name]: highlighted[TOOLS.hideDrawings.name],
        [TOOLS.hideText.name]: highlighted[TOOLS.hideText.name],
        [TOOLS.hideHighlights.name]: highlighted[TOOLS.hideHighlights.name],
      });

      setShowResetModal(true);
    },
    hideText: () => {
      isDrawingToolActive && disableDrawingTool();
      handleHighlighted();
      isColorsSectionVisible && setColorsSectionVisible(false);
      saveTextVisibilityUpdates(styledWordState.isHiddenTextMode, !styledWordState.isHiddenTextMode);
      hideText();
    },
    undo: handleUndoClick,
    redo: handleRedoClick,
    imageEditor: handleImageEditorClick,
    save: openSaveModal,
    close: () => {
      if (!handleLeavePage(onClose, false)) {
        setColorsSectionVisible(false);
        setDrawingToolActive(false);
        setLabelsSectionVisible(false);
        setHighlighted(DEFAULT_STATE);

        onClose();
      }
    },
  };

  // eslint-disable-next-line complexity
  const isDisabled = (tool: string): boolean => {
    switch (tool) {
      case TOOLS.addLabel.name:
        return isLabelsLimitReached;
      case TOOLS.hideNotes.name:
        return !isNotesExist;
      case TOOLS.addNote.name:
        return isNotesManuallyHidden;
      case TOOLS.hideLabel.name:
        return !showHideAllLabels(0);
      case TOOLS.pen.name:
        return areDrawingsHidden;
      case TOOLS.hideDrawings.name:
        return lines[mode] && lines[mode].length === 0;
      case TOOLS.highlight.name:
        return isTextHidden;
      case TOOLS.hideHighlights.name:
        return isTextHidden || !isAnyWordHighlighted;
      case TOOLS.save.name:
        return !isEventsNotEmpty;
      case TOOLS.undo.name:
        return !isEventsNotEmpty;
      case TOOLS.redo.name:
        return !undoneEvents.length;
      case TOOLS.reset.name:
        return !isEventsNotEmpty;
      default:
        return false;
    }
  };

  const onLabelKeyDown = (event: React.KeyboardEvent, id: number) => {
    const { key } = event;

    if (key == 'Enter' || key === ' ') {
      event.preventDefault();
      labelClick && labelClick(id);
    }
  };

  const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    allLabelsClick && allLabelsClick(event.target.checked);
  };

  const handleReset = () => {
    while (events.length > 0) {
      handleUndoClick();
    }

    const { tools } = (exemplar as LaterExemplar | EmergentExemplar) || {};

    if (tools && !isEmpty(tools)) {
      setSavedTools(DEFAULT_STATE);
    } else {
      setHighlighted(DEFAULT_STATE);
    }

    setShowResetModal(false);
  };

  useBeforeUnload(e => handleLeavePage(() => navigate(window.location.href), false, e));

  const saveDropdownOptions = [
    { name: 'Save as new text', callback: openSaveModal },
    { name: 'Save', callback: () => saveToolRef?.current?.forceSave(update, true) },
  ];

  const iconColor = getStageColors(stage).main;

  const getToolButtonTooltipText = (isNotesExist: boolean, toolName: string): string | JSX.Element => {
    const editLabel = getEditLabel(isNotesExist as boolean, toolName);
    switch (toolName) {
      case TOOLS.addLabel.name:
        return isLabelsLimitReached ? AddLabelLimitReached : editLabel;
      default:
        return getEditLabel(isNotesExist as boolean, toolName);
    }
  };

  const getToolButtonAriaLabel = (toolName: string): string => {
    const editLabel = getEditLabel(isNotesExist as boolean, toolName);
    switch (toolName) {
      case TOOLS.addLabel.name:
        return isLabelsLimitReached ? `Label limit reached. Max. ${LIMIT_DRAG_ELEMENTS}.` : editLabel;
      default:
        return editLabel;
    }
  };

  const getNoteTooltipContent = (): boolean => {
    if (!isNotesExist) {
      return false;
    }

    return !isNotesManuallyHidden;
  };

  return (
    <>
      <ResetModal
        showModal={showResetModal}
        setShowModal={setShowResetModal}
        mainColor={panelBgColor}
        onConfirm={handleReset}
      />
      <StyledEditTool data-testid="edit-tool" bgColor={panelBgColor}>
        <ToolsContainer>
          <EditButtonsWrapper>
            {EDIT_TOOL[mode].map((tool: Tool) => {
              let isHighlighted = highlighted[tool.name];
              //TODO: Store highlighted properties in one context
              if (tool.name === 'imageEditor') {
                isHighlighted = styledWordState.imageTool.isVisible;
              }

              return (
                <ToolButton
                  isDisabled={isDisabled(tool.name)}
                  isHighlighted={isHighlighted}
                  bgColor={toolBgColor}
                  withDisabledStyle
                  showTooltip={!isColorsSectionVisible && !isDrawingToolActive}
                  tooltipContent={getToolButtonTooltipText(getNoteTooltipContent(), tool.name)}
                  key={uuid()}
                  ariaLabel={getToolButtonAriaLabel(tool.name)}
                  onClick={functions[tool.name]}
                  icon={getEditIcon(isNotesManuallyHidden, isTextHidden, tool.name, iconColor)}
                  testId={tool.name}
                />
              );
            })}
            {isColorsSectionVisible && (
              <ColorPicker
                chosenColor={styledWordState.color}
                handleColorPick={handleColorPick}
                colors={COLOR_PICKER}
                colorPickerPosition={colorPickerPosition}
              />
            )}
            {isDrawingToolActive && (
              <ColorPicker
                chosenColor={drawingColor}
                handleColorPick={handleDrawingToolColor}
                colors={DRAWING_TOOL_COLORS}
                colorPickerPosition={colorPickerPosition}
              />
            )}
            {isLabelsSectionVisible && (
              <StyledLabelTool data-testid="label-tool" coords={colorPickerPosition}>
                {labels?.length && (
                  <>
                    {labels
                      .sort((a: ILabel, b: ILabel) => {
                        return +a.id > (+b?.id as number) ? 1 : -1;
                      })
                      .map(label => {
                        const { id, isHidden, text, isManuallyHidden } = label;
                        const ariaLabelForLabel = `click to ${isManuallyHidden ? 'show' : 'hide'} ${text} label`;
                        const children = (
                          <>
                            <StyledLabelIcon>
                              {isManuallyHidden ? RemoveRedEyeIcon(redEyeLabel) : SetRedEyeIcon(redEyeLabel)}
                            </StyledLabelIcon>
                            <StyledToolLabelText>{text}</StyledToolLabelText>
                          </>
                        );

                        return (
                          !isHidden &&
                          (!isTouchDevice() ? (
                            <StyledToolLabel
                              data-testid="label"
                              key={id}
                              onClick={() => labelClick && labelClick(id)}
                              onKeyDown={event => onLabelKeyDown(event, id)}
                              tabIndex={0}
                              onPointerEnter={() => onLabelOn && onLabelOn(id)}
                              onPointerLeave={onLabelOut}
                              aria-label={ariaLabelForLabel}
                            >
                              {children}
                            </StyledToolLabel>
                          ) : (
                            <StyledToolLabel
                              key={id}
                              onClick={() => labelClick && labelClick(id)}
                              onKeyDown={event => onLabelKeyDown(event, id)}
                              tabIndex={0}
                              aria-label={ariaLabelForLabel}
                            >
                              {children}
                            </StyledToolLabel>
                          ))
                        );
                      })}
                    {showHideAllLabels(1) && (
                      <>
                        <StyledBorderWrapper color={COLORS.gallery} />
                        <StyledToolLabel
                          key={'words-page-hide-label-tool'}
                          data-testid="hide-all"
                          height="39px"
                          aria-label={ariaLabelForAllLabels}
                        >
                          <StyledLabelIcon>
                            <Checkbox
                              testId="hide-all-input"
                              key={'words-page-hide-label-checkbox'}
                              checked={!isAnyLabelShown}
                              onChange={event => handleCheckBoxChange(event)}
                              inputStyle={{
                                width: '16px',
                                height: '16px',
                                color: COLORS.alto,
                                margin: '5px 10px 0 0',
                              }}
                              labelStyle={{ fontSize: '14px' }}
                              labelText="See all"
                            />
                          </StyledLabelIcon>
                          <StyledLabelTooltip />
                        </StyledToolLabel>
                      </>
                    )}
                  </>
                )}
              </StyledLabelTool>
            )}
          </EditButtonsWrapper>
          <SaveCloseButtonsWrapper>
            <ToolButton
              isHighlighted={false}
              bgColor={toolBgColor}
              showTooltip={true}
              tooltipContent={isSavedExemplar ? 'Save' : TOOLS.save.tooltipText}
              isDropdown={isSavedExemplar}
              dropdownOptions={saveDropdownOptions}
              isDisabled={isDisabled(TOOLS.save.name)}
              withDisabledStyle
              key={uuid()}
              ariaLabel={getEditLabel(isNotesExist as boolean, TOOLS.save.name)}
              onClick={functions[TOOLS.save.name]}
              icon={getEditIcon(isNotesManuallyHidden, isTextHidden, TOOLS.save.name, iconColor)}
              testId={TOOLS.save.name}
            />
            <ToolButton
              isHighlighted={false}
              bgColor={toolBgColor}
              showTooltip={true}
              tooltipContent={TOOLS.close.tooltipText}
              isDisabled={isDisabled(TOOLS.close.name)}
              withDisabledStyle
              key={uuid()}
              ariaLabel={getEditLabel(isNotesExist as boolean, TOOLS.close.name)}
              onClick={functions[TOOLS.close.name]}
              icon={getEditIcon(isNotesManuallyHidden, isTextHidden, TOOLS.close.name, iconColor)}
              testId={TOOLS.close.name}
            />
          </SaveCloseButtonsWrapper>
        </ToolsContainer>
      </StyledEditTool>
      {showTextEditor && (
        <TextEditor
          exemplar={exemplarState}
          defaultExemplar={exemplar}
          stage={stage}
          page={mode}
          onClose={closeTextEditor}
          onTextEdited={handleEditText}
        />
      )}
      <SaveTool
        ref={saveToolRef}
        color={{ main: panelBgColor, light: lightColor }}
        showSaveModal={showSaveModal}
        closeSaveModal={closeSaveModal}
        save={saveAsText}
      />
      {isLeaveModalShown && (
        <LeaveEditingModal
          stage={stage}
          isSavedExemplar={isSavedExemplar}
          exemplarName={exemplar?.title}
          onClose={closeLeaveModal}
          onConfirmLeaving={() => {
            closeLeaveModal();
            clearEdits();
            preventedAction();
          }}
          onSave={() => {
            closeLeaveModal();
            openSaveModal();
          }}
          onUpdate={() => {
            closeLeaveModal();
            saveToolRef?.current?.forceSave(update, true);
          }}
        />
      )}
    </>
  );
};
